/**
 * SOLID Review
 * S — Strong. One concern: whether the name modal is open — including the auto-open for a new guest.
 * O — Strong. A new auto-open condition is the caller's boolean; a new trigger a call of `open`.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. Open, close, and the view-model surface.
 * D — Strong. Its own state; no port.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions, type IConfigReaction } from './Reactions';

export interface IStateIdentityModal {
  readonly isOpen: boolean;
}

const CLOSED: IStateIdentityModal = { isOpen: false };
const OPEN: Partial<IStateIdentityModal> = { isOpen: true };
const SHUT: Partial<IStateIdentityModal> = { isOpen: false };

/**
 * THE NAME MODAL'S OPEN STATE (frontend-framework-oo P2, 2026-10-29, §28.294) — `hooks/useIdentityModal.ts`: auto-opens whenever
 * `shouldAutoOpen` becomes true (a new guest with no JWT and no stored identity); `open`/`close` cover the manual triggers.
 */
export class IdentityModalController implements IServiceViewModel<IStateIdentityModal> {
  private readonly state = new ObservableState<IStateIdentityModal>(CLOSED);
  private readonly declared: ReadonlyArray<IConfigReaction<readonly [shouldAutoOpen: boolean]>> = [{
    deps: (inputs) => inputs,
    run: ([shouldAutoOpen]) => {
      if (shouldAutoOpen) this.state.update(OPEN);
    },
  }];
  private readonly reactions = new Reactions<readonly [shouldAutoOpen: boolean]>(this.declared);

  readonly open = (): void => this.state.update(OPEN);
  readonly close = (): void => this.state.update(SHUT);

  /** Its reaction over the facts of the screen's model that composes it (AppRoot's, P3): whether to open by itself. */
  reactionsOver<TFacts>(shouldAutoOpenOf: (facts: TFacts) => boolean): Array<IConfigReaction<TFacts>> {
    return Reactions.over(this.declared, (facts: TFacts) => [shouldAutoOpenOf(facts)] as const);
  }

  getSnapshot(): IStateIdentityModal {
    return this.state.get();
  }

  subscribe(listener: () => void): () => void {
    return this.state.subscribe(listener);
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(inputs: readonly unknown[]): void {
    this.reactions.stopChanged(inputs as readonly [boolean]);
  }

  commit(inputs: readonly unknown[]): void {
    this.reactions.startChanged(inputs as readonly [boolean]);
  }
}
